import React, { useEffect } from 'react';

import { ResourceMetricsDisplay } from 'pages/reports/components/ResourceMetricsDisplay/ResourceMetricsDisplay';
import { ReportToolTip } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { orderBy, SortDescriptor } from 'utils/dataQuery';
import { buildExportFileName, GenerateUUID } from 'utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { EResourceType } from 'pages/reports/types';
import { useNotification } from 'app/notifications';
import { ReportSection } from 'apis/ReportApi';
import { Card, Table } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { CSVLink } from 'react-csv';

import 'pages/reports/components/ChartInfoBox/styles/chart-info-box.scss';
import 'pages/reports/components/Report69/Report69.scss';

import {
    calculateHourMinFormat,
    formatDate,
    getUniqueStudentNames,
    MAX_TABLE_HEIGHT,
    normalizeScore,
    renderScore,
    ResourceTypeLabelsList,
    sortableHeader
} from '../ReportFunctions';
import { createSortChangeHandler, GridSortChangeEvent } from '../sortUtils';

interface Report69Props {
    reportSections: ReportSection[];
}

export const Report69 = ({
    reportSections
}: Report69Props) => {

    const { formatMessage } = useIntl();

    // State Management
    const [state, setState] = useSuperState({
        exportFileName: 'CourseSummaryReport_Student_Outcomes.csv',
        exportRows: [] as any[],
        showResourcesSelected: ResourceTypeLabelsList,
        sort: [] as SortDescriptor[],
        students: [] as any[],
        tableKey: 'tableKey',
        tableRows: [] as any[]
    });

    const { exportFileName, exportRows, showResourcesSelected, sort, students, tableKey, tableRows } = state;

    const { createToast } = useNotification();

    // Filter rows based on selected resource types
    const selectedValues = showResourcesSelected.map(selected => selected.value);
    const allKnownValues = ResourceTypeLabelsList.map(r => r.value);
    const filteredRows = tableRows.filter(row => selectedValues.includes(row.ResourceTypeID) || !allKnownValues.includes(row.ResourceTypeID)
    );

    const sortedData = orderBy(filteredRows, sort);
    const displayData = sortedData.slice(0, 500);

    const getSortableValue = (item: any, field?: string) => {

        if (!field) return null;
        if (field === 'lastAccess') {

            return item[field] ? new Date(item[field]).getTime() : null;

        }
        if (field === 'avgScore') return item[field] !== null ? item[field] : 0;
        if (field === 'startedPercent' || field === 'completedPercent' || field === 'avgTime') {

            return item[field] || 0;

        }
        const value = item[field];
        return value;

    };

    const handleSortChange = (event: GridSortChangeEvent) => {

        createSortChangeHandler(event, sort, val => setState({ sort: val }), getSortableValue);

    };

    const buildStudentExport = () => {

        const rows = filteredRows.map((row: any) => ({
            [formatMessage({ id: 'average_score.3cb75673', defaultMessage: 'Average Score' })]: row.avgScore !== null ? `${Math.round(row.avgScore)}%` : formatMessage({ id: 'no_score.defd94f7', defaultMessage: 'No Score' }),
            [formatMessage({ id: 'average_time_spent.411b3ea0', defaultMessage: 'Average Time Spent' })]: calculateHourMinFormat(row.avgTime),
            [formatMessage({ id: 'completed.cd3d7862', defaultMessage: 'Completed' })]: `${row.completedPercent}% (${row.completedCount})`,
            [formatMessage({ id: 'last_access.6fd7ff0b', defaultMessage: 'Last Access' })]: row.lastAccess ? formatDate(row.lastAccess) : '---',
            [formatMessage({ id: 'resource.6e70fb89', defaultMessage: 'Resource' })]: EResourceType[row.ResourceTypeID],
            [formatMessage({ id: 'resource_name.c8bbe57c', defaultMessage: 'Resource Name' })]: row.ResourceName || '---',
            [formatMessage({ id: 'started.d612a775', defaultMessage: 'Started' })]: `${row.startedPercent}% (${row.startedCount})`
        }));
        return rows;

    };

    const handleExportCSV = () => {

        const reportName = reportSections[0]?.Title || 'Student Course Detail Report';

        const fileName = buildExportFileName('Report', reportName, 'csv');
        setState({ exportFileName: fileName });
        const dataRows = buildStudentExport();
        setState({ exportRows: dataRows });
        setTimeout(() => {

            const csvLink = document.getElementById('csvLink');
            csvLink?.click();

        }, 0);

    };

    // Aggregate data by resource
    const aggregateDataByResource = (data: any[]) => {

        const resourceMap: Record<string, any> = {};

        data.forEach((item: any) => {

            const key = `${item.ResourceTypeID}-${item.ResourceName}`;
            if (!resourceMap[key]) {

                resourceMap[key] = {
                    ResourceName: item.ResourceName,
                    ResourceTypeID: item.ResourceTypeID,
                    studentRecordsMap: {}
                };

            }

            // Group by student within each resource
            const studentId = item.StudentProfileId;
            if (!resourceMap[key].studentRecordsMap[studentId]) {

                resourceMap[key].studentRecordsMap[studentId] = [];

            }
            resourceMap[key].studentRecordsMap[studentId].push(item);

        });

        return Object.values(resourceMap).map((resource: any) => {

            // Aggregate each student's multiple records for this resource
            const students = Object.values(resource.studentRecordsMap).map((records: any) => {

                // Sum time and attempts, take max score for each student
                const validScores = records.map((r: any) => r.HighestScore).filter((s: any) => s !== null && s !== undefined);
                return {
                    Attempts: records.reduce((sum: number, r: any) => sum + (r.Attempts || 0), 0),
                    CourseDateCompleted: records.find((r: any) => r.CourseDateCompleted)?.CourseDateCompleted,
                    CourseLastAccessDate: records.reduce((latest: string | null, r: any) => {

                        if (!r.CourseLastAccessDate) return latest;
                        if (!latest) return r.CourseLastAccessDate;
                        return new Date(r.CourseLastAccessDate) > new Date(latest) ? r.CourseLastAccessDate : latest;

                    }, null),
                    HighestScore: validScores.length > 0 ? Math.max(...validScores) : null,
                    StudentProfileId: records[0].StudentProfileId,
                    TimeSpentInMinutes: records.reduce((sum: number, r: any) => sum + (r.TimeSpentInMinutes || 0), 0)
                };

            });

            const totalStudents = students.length;

            // Calculate started count (students with attempts or last access)
            const startedCount = students.filter((s: any) => s.Attempts > 0).length;
            const startedPercent = totalStudents > 0 ? Math.round((startedCount / totalStudents) * 100) : 0;

            // Calculate completed count
            const completedCount = students.filter((s: any) => s.CourseDateCompleted).length;
            const completedPercent = totalStudents > 0 ? Math.round((completedCount / totalStudents) * 100) : 0;

            // Calculate average time spent
            const totalTime = students.reduce((sum: number, s: any) => sum + (s.TimeSpentInMinutes || 0), 0
            );
            const avgTime = totalStudents > 0 ? totalTime / totalStudents : 0;

            // Calculate average score
            const scores = students
                .filter((s: any) => s.Attempts > 0) // Only include students with attempts
                .map((s: any) => s.HighestScore)
                .filter((score: any) => score !== null && score !== undefined);
            const avgScore = scores.length > 0
                ? Math.floor(scores.reduce((sum: number, score: number) => sum + normalizeScore(score), 0) / scores.length)
                : null;

            // Find most recent last access
            const lastAccessDates = students
                .map((s: any) => s.CourseLastAccessDate)
                .filter((d: any) => d);
            const lastAccess = lastAccessDates.length > 0
                ? new Date(Math.max(...lastAccessDates.map((d: any) => new Date(d).getTime())))
                : null;

            return {
                avgScore,
                avgTime,
                completedCount,
                completedPercent,
                lastAccess,
                ResourceName: resource.ResourceName,
                ResourceTypeID: resource.ResourceTypeID,
                startedCount,
                startedPercent,
                totalStudents
            };

        });

    };

    // Initialize data on mount
    useEffect(() => {

        setState({ students: getUniqueStudentNames(reportSections[0].Components[0].Results?.Data) });
        const aggregatedData = aggregateDataByResource(reportSections[0].Components[0].Results?.Data ?? []);
        setState({ tableRows: aggregatedData });

    }, []);

    useEffect(() => {

        setState({ tableKey: GenerateUUID() });

    }, []);

    useEffect(() => {

        setState({ tableKey: GenerateUUID() });
        const rawData = reportSections[0].Components[0].Results?.Data ?? [];
        const aggregatedData = aggregateDataByResource(rawData);
        setState({ tableRows: aggregatedData });

    }, [showResourcesSelected]);

    useEffect(() => {

        if (filteredRows.length > 500) {

            createToast(
                formatMessage(
                    { id: 'showing_first_shown_records_of_total_ple.18a484b7', defaultMessage: 'Showing first {shown} records of {total}. Please refine your filters to narrow the results.' },
                    { shown: 500, total: filteredRows.length }
                ),
                'warning'
            );

        }

    }, [filteredRows.length]);

    // Calculate resource metrics - averages across all students (not affected by filters)
    const resourceMetrics = React.useMemo(() => {

        const rawData = reportSections[0].Components[0].Results?.Data ?? [];

        if (rawData.length === 0) {

            return {
                averageScore: 0,
                averageTimeSpent: 0,
                resourcesCompletedPercent: 0,
                resourcesStartedPercent: 0
            };

        }

        // Count student-resource combinations that have been started
        const startedCount = rawData.filter((row: any) => row.Attempts > 0 || row.CourseLastAccessDate
        ).length;
        const resourcesStartedPercent = Math.round((startedCount / rawData.length) * 100);

        // Count student-resource combinations that are completed
        const completedCount = rawData.filter((row: any) => row.CourseDateCompleted).length;
        const resourcesCompletedPercent = Math.round((completedCount / rawData.length) * 100);

        // Calculate average score across all student-resource combinations
        const scoresArray = rawData
            .map((row: any) => row.HighestScore)
            .filter((score: any) => score !== null && score !== undefined);
        const averageScore = scoresArray.length > 0
            ? Math.floor(scoresArray.reduce((sum: number, score: number) => sum + normalizeScore(score), 0) / scoresArray.length)
            : 0;

        // Calculate average time spent per student (sum all time / number of students)
        const uniqueStudents = new Set(rawData.map((row: any) => row.StudentProfileId));
        const totalMinutes = rawData.reduce((sum: number, row: any) => sum + (row.TimeSpentInMinutes || 0), 0
        );
        const averageTimeSpent = uniqueStudents.size > 0
            ? Math.round(totalMinutes / uniqueStudents.size)
            : 0;

        return {
            averageScore,
            averageTimeSpent,
            resourcesCompletedPercent,
            resourcesStartedPercent
        };

    }, [reportSections]);

    return (
        <>
            <ResourceMetricsDisplay
                calculateHourMinFormat={calculateHourMinFormat}
                resourceMetrics={resourceMetrics}
            />

            <Card className="mt-4">
                <CSVLink
                    className="d-none"
                    data={exportRows}
                    filename={exportFileName}
                    id="csvLink"
                    type="button"
                >
                </CSVLink>
                <div className="p-3 d-flex justify-content-between">
                    <div className="d-flex">
                        <GMetrixDropdown
                            multiSelect
                            dataItemKey="value"
                            dataItems={ResourceTypeLabelsList}
                            filterable={false}
                            label={formatMessage({ id: 'show_course_resources.a7ff7100', defaultMessage: 'Show Course Resources' })}
                            onChange={e => setState({ showResourcesSelected: e as any })}
                            style={{ maxWidth: '250px' }}
                            textField="name"
                            value={showResourcesSelected}
                        />
                    </div>
                    <div className="mt-4">
                        <GMetrixButton onClick={handleExportCSV} variant="primary">
                            <FormattedMessage defaultMessage="Export to CSV" id="export_to_csv.e1de1359" />
                        </GMetrixButton>
                    </div>
                </div>
                <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                    <Table bordered hover className="kendo-table sticky-table-header mb-0">
                        <thead>
                            <tr>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'ResourceTypeID' }] })}
                                    style={{ cursor: 'pointer', left: 0, minWidth: 200, position: 'sticky', zIndex: 1 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'course_resources_available_to_students.8d374da7', defaultMessage: 'Course resources available to students' })} trigger="hover">
                                            <span className="d-flex justify-content-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <FormattedMessage defaultMessage="Resources" id="resources.73e9320e" />
                                            </span>
                                        </ReportToolTip>,
                                        'ResourceTypeID',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'startedPercent' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'percent_of_students_who_launched_this_re.19a27828', defaultMessage: 'Percent of students who launched this resource or section at least once.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Started" id="started.d612a775" />
                                            </span>
                                        </ReportToolTip>,
                                        'startedPercent',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'completedPercent' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'percent_of_students_who_completed_this_r.40e756dd', defaultMessage: 'Percent of students who completed this resource or section.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Completed" id="completed.cd3d7862" />
                                            </span>
                                        </ReportToolTip>,
                                        'completedPercent',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'avgTime' }] })}
                                    style={{ cursor: 'pointer', minWidth: 180 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'average_of_student_time_spent_on_course.0d1e6b0a', defaultMessage: 'Average of student time spent on course resources within the selected Date Window.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Average Time Spent" id="average_time_spent.411b3ea0" />
                                            </span>
                                        </ReportToolTip>,
                                        'avgTime',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'avgScore' }] })}
                                    style={{ cursor: 'pointer', minWidth: 160 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'average_of_students_highest_scores_acros.df233ca5', defaultMessage: 'Average of students\' highest scores across completed scored resources.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Average Score" id="average_score.3cb75673" />
                                            </span>
                                        </ReportToolTip>,
                                        'avgScore',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'lastAccess' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip tooltip={formatMessage({ id: 'most_recent_student_interaction_with_thi.f6ecbeea', defaultMessage: 'Most recent student interaction with this resource or section.' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Last Access" id="last_access.6fd7ff0b" />
                                            </span>
                                        </ReportToolTip>,
                                        'lastAccess',
                                        sort
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.map((item: any, rowIndex: number) => (
                                <tr key={`${item.ResourceName}-${rowIndex}`}>
                                    <td className="text-center" style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                        <ReportToolTip copyText={item.ResourceName} id={`${item.ResourceName}-current-metric`} tooltip={item.ResourceName} trigger="hover">
                                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.ResourceName}
                                            </span>
                                        </ReportToolTip>
                                    </td>
                                    <td className="text-center">
                                        {item.startedPercent}
                                        % (
                                        {item.startedCount}
                                        )
                                    </td>
                                    <td className="text-center">
                                        {item.completedPercent}
                                        % (
                                        {item.completedCount}
                                        )
                                    </td>
                                    <td className="text-center">
                                        {calculateHourMinFormat(item.avgTime)}
                                    </td>
                                    <td className="text-center">
                                        {item.avgScore !== null
                                            ? renderScore(item.avgScore, true)
                                            : <FormattedMessage defaultMessage="No Score" id="no_score.defd94f7" />}
                                    </td>
                                    <td className="text-center">
                                        {item.lastAccess ? formatDate(item.lastAccess) : '---'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

            </Card>
        </>
    );

};